import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { replyLanguage, toSpeech } from "./speechText";
import { splitSpeechText } from "./speechChunks";
import {
  checkSpeechAbort, fetchTtsChunk, playAudioBuffer, playSpeechChunks,
  waitForAudioResume,
} from "./speechAudio";

// Holds the widget's voice playback so the speak buttons in MessagesArea and
// the lip-sync in the avatar read the same audio: the AnalyserNode exposed here
// is sampled every frame by whichever avatar is on screen, which turns the
// waveform into morph-target influences (3D) or mouth frames (sprite) — see
// avatar/voiceMeter.
const AvatarSpeechContext = createContext(null);

// The window the voice meter measures: 1024 samples is ~21 ms at 48 kHz, short
// enough to see each syllable rise and fall.
const FFT_SIZE = 1024;

// `voice` is the avatar's voice name (avatars.js), mapped to a neural voice by
// the backend.
export const AvatarSpeechProvider = ({ voice, botId, children }) => {
  const [autoSpeak, setAutoSpeakState] = useState(false);
  const [speakingId, setSpeakingId] = useState(null);
  const [loadingId, setLoadingId] = useState(null);
  const [speechError, setSpeechError] = useState(null);
  const audioCtxRef = useRef(null);
  const analyserRef = useRef(null);
  const sessionRef = useRef(null);
  const retryRef = useRef(null);

  const stop = useCallback(() => {
    const session = sessionRef.current;
    sessionRef.current = null;
    session?.controller.abort();
    retryRef.current = null;
    setSpeakingId(null);
    setLoadingId(null);
    setSpeechError(null);
  }, []);

  useEffect(() => stop, [stop]);

  const setAutoSpeak = useCallback((enabled) => {
    setAutoSpeakState(enabled);
    if (!enabled) stop();
  }, [stop]);

  const toggleSpeak = useCallback(async (id, message) => {
    // Read the session ref so even two clicks in one render cancel correctly.
    if (sessionRef.current?.id === id) {
      stop();
      return;
    }
    const retry = retryRef.current?.id === id ? retryRef.current : null;
    stop();
    const session = {
      id,
      voice: retry?.voice ?? voice,
      language: retry?.language ?? replyLanguage(message),
      botId: retry?.botId ?? botId,
      chunks: retry?.chunks,
      cursor: retry?.cursor ?? 0,
      controller: new AbortController(),
    };
    sessionRef.current = session;
    const { signal } = session.controller;
    const active = () => sessionRef.current === session && !signal.aborted;
    setLoadingId(id);

    try {
      if (!audioCtxRef.current || audioCtxRef.current.state === "closed") {
        const AudioCtx = window.AudioContext || window.webkitAudioContext;
        if (!AudioCtx) throw new Error("Audio playback is unavailable in this browser.");
        audioCtxRef.current = new AudioCtx();
        analyserRef.current = audioCtxRef.current.createAnalyser();
        analyserRef.current.fftSize = FFT_SIZE;
      }
      const audioCtx = audioCtxRef.current;
      // Call during the click, before any network await, to retain activation.
      // Handle rejection now, even if synthesis takes longer than resume.
      const resuming = audioCtx.resume().then(
        () => ({}), (error) => ({ error }),
      );
      // Sentence boundaries can leave standalone punctuation/whitespace.
      // These carry no speech and the backend correctly rejects them.
      const readable = session.language === "zh"
        ? /[A-Za-z0-9㐀-䶿一-鿿豈-﫿]/
        : /[A-Za-z0-9]/;
      session.chunks = session.chunks ?? splitSpeechText(
        toSpeech(message), session.language,
      ).filter((chunk) => readable.test(chunk));
      if (!session.chunks.length) throw new Error("There is no text to read aloud.");

      await playSpeechChunks({
        chunks: session.chunks,
        startIndex: session.cursor,
        signal,
        loadAudio: async (chunk) => {
          const buffer = await fetchTtsChunk(
            chunk, session.voice, session.botId, session.language, signal,
          );
          checkSpeechAbort(signal);
          return audioCtx.decodeAudioData(buffer);
        },
        playAudio: async (audio) => {
          checkSpeechAbort(signal);
          if (audioCtx.state !== "running") await waitForAudioResume(resuming, signal);
          checkSpeechAbort(signal);
          if (audioCtx.state !== "running") {
            throw new Error("Audio is blocked. Tap Retry to enable playback.");
          }
          return playAudioBuffer(audioCtx, analyserRef.current, audio, signal);
        },
        onWaiting: () => {
          if (active()) setLoadingId(id);
        },
        onPlaying: () => {
          if (active()) {
            setLoadingId(null);
            setSpeakingId(id);
          }
        },
        onProgress: (cursor) => { session.cursor = cursor; },
      });
      if (active()) stop();
    } catch (error) {
      if (!active()) return;
      sessionRef.current = null;
      session.controller.abort();
      // Keep only text and the next unread position, never old audio buffers.
      retryRef.current = session.chunks ? {
        id, chunks: session.chunks, cursor: session.cursor,
        voice: session.voice, language: session.language, botId: session.botId,
      } : null;
      setLoadingId(null);
      setSpeakingId(null);
      setSpeechError({
        id,
        message: error.message || "Speech is unavailable. Retry to continue.",
      });
    }
  }, [voice, botId, stop]);

  const value = useMemo(() => ({
    autoSpeak, setAutoSpeak, speakingId, loadingId, speechError, toggleSpeak, stop,
    // All chunks use the same analyser, keeping the avatar's mouth in sync.
    analyserRef,
  }), [autoSpeak, setAutoSpeak, speakingId, loadingId, speechError, toggleSpeak, stop]);

  return (
    <AvatarSpeechContext.Provider value={value}>
      {children}
    </AvatarSpeechContext.Provider>
  );
};

export const useAvatarSpeech = () => {
  const ctx = useContext(AvatarSpeechContext);
  if (!ctx) {
    throw new Error("useAvatarSpeech must be used inside <AvatarSpeechProvider>");
  }
  return ctx;
};
